import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getProjects, testPipeline } from '../../api/admin'

const statusColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  processing: 'bg-blue-100 text-blue-600',
  waiting_input: 'bg-yellow-100 text-yellow-600',
  completed: 'bg-green-100 text-green-600',
  failed: 'bg-red-100 text-red-600',
}

export default function ProjectsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [testUrl, setTestUrl] = useState('')
  const [showTestForm, setShowTestForm] = useState(false)

  const { data: projects, isLoading, refetch } = useQuery({
    queryKey: ['admin-projects', statusFilter],
    queryFn: () => getProjects({ status: statusFilter || undefined, limit: 50 }),
  })

  const testMutation = useMutation({
    mutationFn: () => testPipeline(testUrl),
    onSuccess: (data) => {
      alert(`Pipeline started! Project ID: ${data.project_id}\n\n${data.message}`)
      setTestUrl('')
      setShowTestForm(false)
      refetch()
    },
    onError: (error: Error) => {
      alert(`Error: ${error.message}`)
    },
  })

  const handleTest = (e: React.FormEvent) => {
    e.preventDefault()
    if (testUrl.trim()) {
      testMutation.mutate()
    }
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Projects</h1>
          <p className="text-gray-500 text-sm mt-1">View all user projects</p>
        </div>
        <button
          onClick={() => setShowTestForm(!showTestForm)}
          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition"
        >
          Test Pipeline
        </button>
      </div>

      {/* Test form */}
      {showTestForm && (
        <div className="mb-6 bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Test Pipeline</h3>
          <form onSubmit={handleTest} className="flex gap-4">
            <input
              type="url"
              value={testUrl}
              onChange={(e) => setTestUrl(e.target.value)}
              placeholder="Enter audio/video URL to test..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              required
            />
            <button
              type="submit"
              disabled={testMutation.isPending}
              className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-lg transition disabled:opacity-50"
            >
              {testMutation.isPending ? 'Starting...' : 'Start'}
            </button>
          </form>
          <p className="text-sm text-gray-500 mt-2">
            This will create a project and start the pipeline without using Telegram.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setStatusFilter('')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            statusFilter === '' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {Object.keys(statusColors).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              statusFilter === status ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Projects table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>
        ) : projects && projects.length > 0 ? (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-800">
                    #{project.id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div className="max-w-xs truncate" title={project.title || project.source_url}>
                      {project.title || project.source_url.substring(0, 40) + '...'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    User #{project.user_id}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      statusColors[project.status] || 'bg-gray-100 text-gray-600'
                    }`}>
                      {project.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {project.current_stage_index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(project.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No projects found
          </div>
        )}
      </div>
    </div>
  )
}
